import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import {
    Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, CircularProgress, Alert, Skeleton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon        from '@mui/icons-material/Add';
import DeleteIcon     from '@mui/icons-material/Delete';
import EditIcon       from '@mui/icons-material/Edit';

import axiosPublic from '../api/axiosPublic';
import axiosAuth   from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const GOLD       = '#C9973A';
const FOREST     = '#4A6741';
const PARCHMENT  = '#F7F3EE';

export default function FaqPage() {
    const { user } = useAuth();
    const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [faqs,    setFaqs]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    // Add dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [question,   setQuestion]   = useState('');
    const [answer,     setAnswer]     = useState('');
    const [saving,     setSaving]     = useState(false);
    const [formError,  setFormError]  = useState('');

    // Delete confirm
    const [deleteId,  setDeleteId]  = useState(null);
    const [deleting,  setDeleting]  = useState(false);

    // Edit dialog
    const [editFaq,       setEditFaq]       = useState(null);
    const [editQuestion,  setEditQuestion]  = useState('');
    const [editAnswer,    setEditAnswer]    = useState('');
    const [editSaving,    setEditSaving]    = useState(false);
    const [editFormError, setEditFormError] = useState('');

    const fetchFaqs = async () => {
        setLoading(true);
        try {
            const res = await axiosPublic.get('/api/faqs/view/generic');
            setFaqs(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError('Failed to load FAQs.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFaqs(); }, []);

    const openAddDialog = () => {
        setQuestion('');
        setAnswer('');
        setFormError('');
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!question.trim() || !answer.trim()) {
            setFormError('Both question and answer are required.');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            await axiosAuth.post('/api/faqs', { question: question.trim(), answer: answer.trim() });
            setDialogOpen(false);
            fetchFaqs();
        } catch (err) {
            setFormError(err?.response?.data?.message || 'Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openEditDialog = (faq) => {
        setEditFaq(faq);
        setEditQuestion(faq.question);
        setEditAnswer(faq.answer);
        setEditFormError('');
    };

    const handleEditSave = async () => {
        if (!editQuestion.trim() || !editAnswer.trim()) {
            setEditFormError('Both question and answer are required.');
            return;
        }
        setEditSaving(true); setEditFormError('');
        try {
            const res = await axiosAuth.put(`/api/faqs/edit/${editFaq.id}`,
                { question: editQuestion.trim(), answer: editAnswer.trim() });
            setFaqs(prev => prev.map(f => f.id === editFaq.id ? res.data : f));
            setEditFaq(null);
        } catch (err) {
            setEditFormError(err?.response?.data?.message || 'Save failed.');
        } finally {
            setEditSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosAuth.delete(`/api/faqs/delete/${deleteId}`);
            setDeleteId(null);
            fetchFaqs();
        } catch {
            setError('Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ bgcolor: PARCHMENT, minHeight: '100vh' }}>
            <Helmet><title>FAQ — HayTour</title></Helmet>

            {/* Banner */}
            <Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 } }}>
                <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.22em', fontWeight: 700 }}>
                    Frequently asked questions
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                    <Typography variant="h4" fontWeight={800}
                                sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif" }}>
                        FAQ
                    </Typography>
                    {isManager && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}
                                sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                    color: 'white', borderRadius: 2, fontWeight: 700 }}>
                            Add FAQ
                        </Button>
                    )}
                </Box>
            </Box>

            <Container maxWidth="md" sx={{ py: 6 }}>
                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

                {loading ? (
                    <Stack spacing={1.5}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                        ))}
                    </Stack>
                ) : faqs.length === 0 ? (
                    <Box sx={{ py: 10, textAlign: 'center', border: '2px dashed rgba(196,98,45,0.2)', borderRadius: 3 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mb: 1 }}>
                            No FAQs yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Check back soon for answers to common questions.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {faqs.map(faq => (
                            <Accordion key={faq.id} elevation={0}
                                       sx={{ border: '1px solid rgba(196,98,45,0.15)', borderRadius: '12px !important',
                                           '&:before': { display: 'none' },
                                           '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(196,98,45,0.1)' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TERRACOTTA }} />}
                                                  sx={{ px: 3, py: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        width: '100%', pr: 1 }}>
                                        <Typography fontWeight={700} sx={{ color: WALNUT }}>
                                            {faq.question}
                                        </Typography>
                                        {isManager && (
                                            <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                                                <IconButton size="small"
                                                    onClick={e => { e.stopPropagation(); openEditDialog(faq); }}
                                                    sx={{ color: TERRACOTTA }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small"
                                                    onClick={e => { e.stopPropagation(); setDeleteId(faq.id); }}
                                                    sx={{ color: '#c62828' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
                                    <ReactMarkdown components={{
                                        p: (props) => <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 0.5 }} {...props} />,
                                    }}>{faq.answer}</ReactMarkdown>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                )}
            </Container>

            {/* Add FAQ Dialog */}
            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)}
                    maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={800}
                             sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Add FAQ
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        <TextField fullWidth size="small" label="Question"
                                   value={question} onChange={e => setQuestion(e.target.value)}
                                   multiline minRows={2} />
                        <TextField fullWidth size="small" label="Answer"
                                   value={answer} onChange={e => setAnswer(e.target.value)}
                                   multiline minRows={3} />
                        {formError && <Alert severity="error" sx={{ borderRadius: 2 }}>{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}
                            sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                color: 'white', borderRadius: 2, fontWeight: 700 }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit FAQ Dialog */}
            <Dialog open={!!editFaq} onClose={() => !editSaving && setEditFaq(null)}
                    maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={800}
                             sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Edit FAQ
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        <TextField fullWidth size="small" label="Question"
                                   value={editQuestion} onChange={e => setEditQuestion(e.target.value)}
                                   multiline minRows={2} />
                        <TextField fullWidth size="small" label="Answer"
                                   value={editAnswer} onChange={e => setEditAnswer(e.target.value)}
                                   multiline minRows={3} />
                        {editFormError && <Alert severity="error" sx={{ borderRadius: 2 }}>{editFormError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setEditFaq(null)} disabled={editSaving}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSave} disabled={editSaving}
                            sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                color: 'white', borderRadius: 2, fontWeight: 700 }}>
                        {editSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ color: '#b71c1c' }}>Delete FAQ</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete this FAQ? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
                            sx={{ borderRadius: 2, fontWeight: 700 }}>
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
